import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  Briefcase,
  Clock,
  DollarSign,
  MessageSquare,
  CheckCircle2,
  TrendingDown,
  TrendingUp,
  Minus,
} from "lucide-react";
import {
  collection,
  doc,
  getDoc,
  addDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";
import useCategories from "../../hooks/useCategories";
import { Button } from "../ui/Button";

export default function ViewOffers() {
  const navigate = useNavigate();
  const { requestId } = useParams();

  const { categories } = useCategories();

  const [request, setRequest] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loadingRequest, setLoadingRequest] = useState(true);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [acceptedOfferId, setAcceptedOfferId] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setLoadingRequest(false);
      setLoadingOffers(false);
      return;
    }

    const unsubRequest = onSnapshot(
      doc(db, "serviceRequests", requestId),
      (snap) => {
        if (snap.exists()) {
          setRequest({ id: snap.id, ...snap.data() });
        } else {
          setRequest(null);
        }
        setLoadingRequest(false);
      },
      (error) => {
        console.error("Failed to load service request:", error);
        setLoadingRequest(false);
      }
    );

    return () => unsubRequest();
  }, [requestId]);

  useEffect(() => {
    if (!requestId) {
      setLoadingOffers(false);
      return;
    }

    const q = query(
      collection(db, "taskerOffers"),
      where("requestedId", "==", requestId)
    );

    const unsubOffers = onSnapshot(
      q,
      async (snap) => {
        try {
          const rawOffers = snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));

          const taskerIds = [...new Set(rawOffers.map((o) => o.taskerId).filter(Boolean))];

          const taskersMap = {};
          await Promise.all(
            taskerIds.map(async (taskerId) => {
              try {
                const taskerSnap = await getDoc(doc(db, "taskers", taskerId));
                if (taskerSnap.exists()) {
                  taskersMap[taskerId] = taskerSnap.data();
                }
              } catch (error) {
                console.error(`Failed to load tasker ${taskerId}:`, error);
              }
            })
          );

          const hydratedOffers = rawOffers.map((offer) => {
            const tasker = taskersMap[offer.taskerId] || {};
            return {
              ...offer,
              taskerName: tasker.name || "Unknown Tasker",
              taskerImage:
                tasker.imageUrl ||
                "https://via.placeholder.com/64?text=T",
              taskerRating: tasker.rating ?? null,
              taskerReviews: tasker.reviewsCount ?? null,
              taskerCompletedJobs: tasker.completedJobs ?? null,
            };
          });

          setOffers(hydratedOffers);
        } catch (error) {
          console.error("Failed to hydrate offers:", error);
          setOffers([]);
        } finally {
          setLoadingOffers(false);
        }
      },
      (error) => {
        console.error("Failed to load offers:", error);
        setLoadingOffers(false);
      }
    );

    return () => unsubOffers();
  }, [requestId]);

  const category = useMemo(() => {
    if (!request?.categoryId) return null;
    return categories.find((c) => c.id === request.categoryId) || null;
  }, [categories, request?.categoryId]);

  const subcategory = useMemo(() => {
    if (!category || !request?.subcategoryId) return null;
    return (
      category.subcategories?.find((s) => s.id === request.subcategoryId) || null
    );
  }, [category, request?.subcategoryId]);
  
  function toRad(value) {
  return (value * Math.PI) / 180;
}

  function calculateDistanceKm(from, to) {
    const lat1 = Number(from?.lat);
    const lng1 = Number(from?.lng);
    const lat2 = Number(to?.lat);
    const lng2 = Number(to?.lng);

    if (
      !Number.isFinite(lat1) ||
      !Number.isFinite(lng1) ||
      !Number.isFinite(lat2) ||
      !Number.isFinite(lng2)
    ) {
      return null;
    }

    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Number((R * c).toFixed(2));
  }

  const handleAcceptOffer = async (offerId) => {
    if (!request) return;
    
    const customerLocation = {
      lat: request.location?.lat ?? null,
      lng: request.location?.lng ?? null,
    };

    let taskerLocation = { lat: null, lng: null };

    const acceptedOffer = offers.find((o) => o.id === offerId);
    if (!acceptedOffer) return;

    try {
      setAccepting(true);

      // get customer info
      let customerName = "";
      try {
        const customerSnap = await getDoc(doc(db, "customers", request.customerId));
        if (customerSnap.exists()) {
          customerName = customerSnap.data().name || "";
        }
      } catch (err) {
        console.error("Failed to load customer:", err);
      }
      
      const taskerSnap = await getDoc(doc(db, "taskers", acceptedOffer.taskerId));
      if (taskerSnap.exists()) {
        const taskerData = taskerSnap.data();
        taskerLocation = {
          lat: taskerData.location?.lat ?? null,
          lng: taskerData.location?.lng ?? null,
        };
      }
      const distanceValue = calculateDistanceKm(customerLocation, taskerLocation);
      const distance = distanceValue == null ? null : Number(distanceValue.toFixed(1));
      // create job first
      const jobRef = await addDoc(collection(db, "jobs"), {
        addressId: request.addressId || null,
        categoryId: request.categoryId || "",
        subcategoryId: request.subcategoryId || "",
        customerId: request.customerId || "",
        customerName: customerName || "",
        location: {
          lat: request.location?.lat ?? null,
          lng: request.location?.lng ?? null,
        },
        locationText: request.locationText || "",
        distance: distance,
        preferredAt: null,
        preferredDate: request.preferredDate || "",
        preferredTime: request.preferredTime || "",
        price: acceptedOffer.offeredPrice ?? request.budget ?? null,
        paymentMethod: request.paymentMethod || "",
        paymentStatus: request.paymentStatus || "unpaid",
        status: "pending_payment",
        progressStatus: null,
        completedAt: null,
        note: acceptedOffer.message || request.description || "",
        taskerId: acceptedOffer.taskerId || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // then update service request
      await updateDoc(doc(db, "serviceRequests", request.id), {
        status: "assigned",
        taskerId: acceptedOffer.taskerId || "",
        acceptedOfferId: acceptedOffer.id,
        updatedAt: serverTimestamp(),
        jobId: jobRef.id,
      });

      setAcceptedOfferId(offerId);

      setTimeout(() => {
        navigate(`/customer/order-status/${jobRef.id}`);
      }, 1500);
    } catch (error) {
      console.error("Failed to accept offer:", error);
      alert("Failed to accept the offer. Please try again.");
    } finally {
      setAccepting(false);
    }
  };

  const getOfferStatusBadge = (offer) => {
    const requestBudget = Number(request?.budget || 0);
    const offerPrice = Number(offer?.offeredPrice || 0);
    const diff = offerPrice - requestBudget;

    if (diff < 0) {
      return (
        <div className="flex items-center gap-1 text-green-600 text-sm">
          <TrendingDown size={16} />
          <span>{Math.abs(diff)} SAR below budget</span>
        </div>
      );
    }

    if (diff > 0) {
      return (
        <div className="flex items-center gap-1 text-orange-600 text-sm">
          <TrendingUp size={16} />
          <span>{diff} SAR above budget</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1 text-blue-600 text-sm">
        <Minus size={16} />
        <span>Matches budget</span>
      </div>
    );
  };

  const formatCreatedAt = (value) => {
    if (!value) return "Recently";

    try {
      const date =
        typeof value?.toDate === "function" ? value.toDate() : new Date(value);

      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Recently";
    }
  };

  if (loadingRequest || loadingOffers) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading offers...</p>
      </div>
    );
  }

  if (!requestId || !request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <h2 className="text-xl text-[#1E3A8A] mb-2">Request not found</h2>
          <p className="text-gray-600 mb-6">
            We could not find this service request.
          </p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  if (acceptedOfferId) {
    const acceptedOffer = offers.find((o) => o.id === acceptedOfferId);

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center animate-in zoom-in duration-300">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={40} className="text-[#006C35]" />
          </div>

          <h2 className="text-2xl text-[#1E3A8A] mb-2">Offer Accepted!</h2>

          <p className="text-gray-600 mb-6">
            You have accepted {acceptedOffer?.taskerName}&apos;s offer. They will be
            notified and will contact you shortly.
          </p>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">Agreed Price:</span>
              <span className="text-2xl text-[#006C35] font-bold">
                {acceptedOffer?.offeredPrice} SAR
              </span>
            </div>

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Estimated Duration:</span>
              <span className="text-gray-700">
                {acceptedOffer?.estimatedDuration || "Unknown"}
              </span>
            </div>
          </div>

          <Button
            onClick={() => navigate("/customer/home")}
            className="w-full bg-[#006C35] hover:bg-[#005428]"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={24} />
          </button>

          <div>
            <h2 className="text-[#1E3A8A]">Offers Received</h2>
            <p className="text-sm text-gray-500">
              {offers.length} tasker{offers.length !== 1 ? "s" : ""} interested
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg text-[#1E3A8A] mb-3">Your Service Request</h3>

          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                {subcategory?.name || request.subcategoryId || "Service Request"}
              </h4>

              <div className="flex items-center gap-2 mb-2">
                {category && (
                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm">
                    {category.icon} {category.name}
                  </span>
                )}

                {subcategory && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm">
                    {subcategory.name}
                  </span>
                )}
              </div>
            </div>

            <div className="text-right">
              <div className="text-xs text-gray-500 mb-1">Your Budget</div>
              <div className="text-2xl text-[#1E3A8A] font-bold">
                {request.budget || 0} SAR
              </div>
            </div>
          </div>

          <p className="text-gray-600 text-sm">
            {request.description || "No description provided."}
          </p>
        </div>

        {offers.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center">
            <p className="text-gray-500">
              No offers received yet. Taskers will send their offers soon.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg text-[#1E3A8A]">Available Offers</h3>
              <span className="text-sm text-gray-500">
                Sorted by price (low to high)
              </span>
            </div>

            {[...offers]
              .sort((a, b) => Number(a.offeredPrice || 0) - Number(b.offeredPrice || 0))
              .map((offer) => (
                <div
                  key={offer.id}
                  className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
                    selectedOfferId === offer.id
                      ? "border-[#006C35]"
                      : "border-transparent hover:border-gray-200"
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <img
                        src={offer.taskerImage}
                        alt={offer.taskerName}
                        className="w-16 h-16 rounded-xl object-cover"
                      />

                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-lg text-[#1E3A8A] mb-1">
                              {offer.taskerName}
                            </h4>

                            <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                              {offer.taskerRating !== null && (
                                <div className="flex items-center gap-1">
                                  <Star
                                    size={16}
                                    className="text-yellow-500 fill-yellow-500"
                                  />
                                  <span className="font-semibold">
                                    {offer.taskerRating}
                                  </span>

                                  {offer.taskerReviews !== null && (
                                    <span className="text-gray-400">
                                      ({offer.taskerReviews} reviews)
                                    </span>
                                  )}
                                </div>
                              )}

                              {offer.taskerCompletedJobs !== null && (
                                <div className="flex items-center gap-1">
                                  <Briefcase
                                    size={16}
                                    className="text-[#006C35]"
                                  />
                                  <span>{offer.taskerCompletedJobs} jobs</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="text-2xl text-[#006C35] font-bold mb-1">
                              {offer.offeredPrice || 0} SAR
                            </div>

                            {getOfferStatusBadge(offer)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Clock size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">
                            Estimated Duration
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            {offer.estimatedDuration || "Unknown"}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <DollarSign size={20} className="text-green-600" />
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Total Cost</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {offer.offeredPrice || 0} SAR (all-inclusive)
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <MessageSquare size={16} />
                        <span className="font-medium">
                          Message from {offer.taskerName.split(" ")[0]}
                        </span>
                      </div>

                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {offer.message || "No message provided."}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <span className="text-xs text-gray-500">
                        Offer sent {formatCreatedAt(offer.createdAt)}
                      </span>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() =>
                            setSelectedOfferId(
                              selectedOfferId === offer.id ? null : offer.id
                            )
                          }
                          className={
                            selectedOfferId === offer.id
                              ? "border-[#006C35] text-[#006C35]"
                              : ""
                          }
                        >
                          {selectedOfferId === offer.id ? "Selected" : "Select"}
                        </Button>

                        <Button
                          onClick={() => handleAcceptOffer(offer.id)}
                          className="bg-[#006C35] hover:bg-[#005428]"
                          disabled={accepting}
                        >
                          <CheckCircle2 size={18} className="mr-2" />
                          {accepting ? "Accepting..." : "Accept Offer"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            Choosing the Right Tasker
          </h4>

          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Compare prices and estimated durations carefully</li>
            <li>• Check tasker ratings and number of completed jobs</li>
            <li>• Read their messages to understand their approach</li>
            <li>• Once you accept an offer, the tasker will be notified immediately</li>
            <li>• You can contact the tasker after accepting to coordinate details</li>
          </ul>
        </div>
      </div>
    </div>
  );
}